import BookingModule from "@/store/modules/booking";
import { PCTypes, SeatType } from "@/types/db/product-category";
import {
  BookingStatus,
  IProductSlotState,
  PaymentStatus,
} from "@/types/db/product-slot-state";
import { computed, watch } from "vue";
import { ElMessageBox } from "element-plus";
import i18n from "@/lang";
import { LogLevel, LogService } from "@/services/logging";
import { deleteUnconfirmedTickets } from "./booking";
import { nowDB } from "../helpers/dates";
import { cloneDeep } from "lodash";
import { changeObjectsStatuses } from "@/api/seats-io/seats-io";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
const selectedBookingUuid = computed(() => {
  return BookingModule.selectedBookingUUID;
});

/**
 * This function is triggered when a holdToken is expired before the user confirm the booking
 * We show a messageBox to the user mentionning that all unconfirmed seating tickets are going to be removed
 */
export async function handleHoldTokenExpiration(bookingUuid: string) {
  // console.log("hi from handleHoldTokenExpiration");
  LogService.info(`handling holdToken expiration of booking ${bookingUuid}`);
  // 1- Check if the booking of this holdToken is still in the list, and see if there is actually any unconfirmed seating pss in it or not
  const isBookingInState = BookingModule.bookingList
    .map((booking) => booking.uuid)
    .includes(bookingUuid);
  if (!isBookingInState) {
    // This booking isn't in state anymore, lets not do anything
    return;
  }

  const concernedBooking = BookingModule.bookings[bookingUuid];
  if (!concernedBooking) {
    // This booking isn't in state anymore, lets not do anything
    // console.log("returning 2");
    return;
  }
  const unconfirmedSeatingPSS = concernedBooking.pssList.filter(
    (ps) =>
      ps.type === PCTypes.SeatedSub &&
      ps.bookingStatus === BookingStatus.Pending
  );
  if (!unconfirmedSeatingPSS) {
    // This booking doesn't have any seating ticket that is not confirmed, lets not do anything
    // console.log("returning 3");
    return;
  }

  if (!unconfirmedSeatingPSS.length) {
    // This booking doesn't have any seating ticket that is not confirmed, lets not do anything
    // console.log("returning 4");
    return;
  }
  // 2- If holdToken of booking A is expired and user is at booking B then we wait till the user goes back to booking A to do point(3)
  if (selectedBookingUuid.value === bookingUuid) {
    await showRemovalSeatsMsgBox(
      unconfirmedSeatingPSS.map((ps) => String(ps.uuid))
    );
  } else {
    console.log("watching");
    LogService.info(
      `user is in a booking different than the one with the expired holdToken, lets watch for selectedBookingUuid`
    );
    // Watching selectedBookingUuid
    watch(
      selectedBookingUuid,
      async () => {
        if (selectedBookingUuid.value === bookingUuid) {
          // So user is back to a booking where the holdToken has expired
          // console.log(
          //   "So user is back to a booking where the holdToken has expired"
          // );
          LogService.info(
            `So user is back to a booking where the holdToken has expired`
          );
          await showRemovalSeatsMsgBox(
            unconfirmedSeatingPSS.map((ps) => String(ps.uuid))
          );
        } else {
          // console.log("user is back to another booking, we will do nothing");
          LogService.info(
            `user is back to another booking, we will do nothing`
          );
          // user is back to another booking, we will do nothing
        }
      },
      { deep: true }
    );
  }
}

async function showRemovalSeatsMsgBox(pssUuids: string[]) {
  // console.log("hi from showRemovalSeatsMsgBox");
  LogService.info(
    `Showing an alert informing user that unconfirmed seating tickets will be removed`
  );
  await ElMessageBox.alert(
    "Session expired, unpaid seating tickets will be removed",
    "Warning",
    {
      showClose: false,
    }
  );
  // console.log(
  //   "removing unconfirmed PSS, at this stage seats io already freed the corresponding seats"
  // );
  const releaseSeats = true;
  await deleteUnconfirmedTickets(pssUuids, releaseSeats);
}

/**
 * This function will build an array grouping seats by their status and their eventKey
 * then contacts seats io to update the status of those seats
 */
export async function changeSeatsStatuses(
  seatingPssList: IProductSlotState[],
  bookingCopy: IProductSlotBooking
) {
  console.log("------------ hi from changeSeatsStatuses");
  // lets build an array of objects holding seats grouped by their eventKey and their seat status
  // lets go with clearest route
  const seatsStatusChanges: {
    event: string;
    objects: { objectId: string; /*pssUuid*/ ticketType: string }[];
    status: string;
    orderId: string;
    holdToken?: string;
  }[] = [
    { event: "", objects: [], status: "booked", orderId: "" },
    { event: "", objects: [], status: "paid", orderId: "" },
    { event: "", objects: [], status: "scanned", orderId: "" },
  ];
  // lets now group PSS with the same eventKey and the same intended seat status together
  for (let i = 0; i < seatingPssList.length; i++) {
    // For GA type we don't change status to "paid" nor to "scanned" even if the pss is "paid" or "scanned", we only set status to "booked" because seats io would create new GA tickets if we supply "paid" or "scanned" statuses
    if (seatingPssList[i].seatType === SeatType.GeneralAdmissionArea) {
      // Lets remember we don't consider the seat if its PSS has already a fullSeat object with the `booked`status because this means the seat is already `booked`at seats io level
      if (
        seatingPssList[i].fullSeat &&
        (seatingPssList[i].fullSeat.status === "booked" ||
          seatingPssList[i].fullSeat.status === "paid" ||
          seatingPssList[i].fullSeat.status === "scanned")
      ) {
        // So this GA ticket is already booked at seats io level lets not consider it
        continue;
      }

      // Lets see if we have already GA tickets with status `booked`and the same eventKey
      // so that we push this GA ticket with them
      const bookedGAObject = seatsStatusChanges.find(
        (st) =>
          st.event === seatingPssList[i].eventKey && st.status === "booked"
      );
      if (bookedGAObject) {
        // Lets push this GA ticket with the already existing GA tickets with the same `booked`status and the same eventKey
        bookedGAObject.objects.push({
          objectId: String(seatingPssList[i].seat),
          ticketType: String(seatingPssList[i].uuid),
        });
      } else {
        // so there is no GA tickets with the same `booked` status and the same eventKey, lets create such an object that will hold these kind of GA tickets
        seatsStatusChanges.push({
          event: String(seatingPssList[i].eventKey),
          objects: [
            {
              objectId: String(seatingPssList[i].seat),
              ticketType: String(seatingPssList[i].uuid),
            },
          ],
          status: "booked",
          orderId: String(BookingModule.selectedBooking?.reference),
          holdToken: String(
            BookingModule.selectedBooking?.seatsIoHoldToken.holdToken
          ),
        });
      }
      continue;
    }

    // 1- Checking for scanned seats
    // Lets remember we don't consider the seat if its PSS has already a fullSeat object with the `scanned`status because this means the seat is already `scanned`at seats io level
    if (seatingPssList[i].scanningStatus && seatingPssList[i].eventKey) {
      if (
        seatingPssList[i].fullSeat &&
        seatingPssList[i].fullSeat.status === "scanned"
      ) {
        // So this seat is already scanned at seats io level lets not consider it
        continue;
      }
      // Lets see if we have already seats with status `scanned`and the same eventKey
      // so that we push this seat with them
      const scannedObject = seatsStatusChanges.find(
        (st) =>
          st.event === seatingPssList[i].eventKey && st.status === "scanned"
      );
      if (scannedObject) {
        // Lets push this seat with the already existing seats with the same `scanned`status and the same eventKey
        scannedObject.objects.push({
          objectId: String(seatingPssList[i].seat),
          ticketType: String(seatingPssList[i].uuid),
        });
      } else {
        // so there is no seats with the same `scanned` status and the same eventKey, lets create such an object that will hold these kind of seats
        seatsStatusChanges.push({
          event: String(seatingPssList[i].eventKey),
          objects: [
            {
              objectId: String(seatingPssList[i].seat),
              ticketType: String(seatingPssList[i].uuid),
            },
          ],
          status: "scanned",
          orderId: String(BookingModule.selectedBooking?.reference),
          holdToken: String(
            BookingModule.selectedBooking?.seatsIoHoldToken.holdToken
          ),
        });
      }
      // `scanned`seat will have `scanned`status, no need to check for `paid`or `booked`statuses
      continue;
    }

    // 2- Checking for paid seats
    if (
      seatingPssList[i].paymentStatus === PaymentStatus.Completed &&
      seatingPssList[i].eventKey
    ) {
      if (
        seatingPssList[i].fullSeat &&
        seatingPssList[i].fullSeat.status === "paid"
      ) {
        // So this seat is already paid at seats io level lets not consider it
        continue;
      }
      // Lets see if we have already seats with status `paid`and the same eventKey
      // so that we push this seat with them
      const paidObject = seatsStatusChanges.find(
        (st) => st.event === seatingPssList[i].eventKey && st.status === "paid"
      );
      if (paidObject) {
        // Lets push this seat with the already existing seats with the same `paid`status and the same eventKey
        paidObject.objects.push({
          objectId: String(seatingPssList[i].seat),
          ticketType: String(seatingPssList[i].uuid),
        });
      } else {
        // so there is no seats with the same `paid` status and the same eventKey, lets create such an object that will hold these kind of seats
        seatsStatusChanges.push({
          event: String(seatingPssList[i].eventKey),
          objects: [
            {
              objectId: String(seatingPssList[i].seat),
              ticketType: String(seatingPssList[i].uuid),
            },
          ],
          status: "paid",
          orderId: String(BookingModule.selectedBooking?.reference),
          holdToken: String(
            BookingModule.selectedBooking?.seatsIoHoldToken.holdToken
          ),
        });
      }
      // `paid`seat will have `paid`status, no need to check for `booked`statuses
      continue;
    }

    // 3- Checking for booked seats
    if (
      seatingPssList[i].bookingStatus === BookingStatus.Confirmed &&
      seatingPssList[i].eventKey
    ) {
      if (
        seatingPssList[i].fullSeat &&
        seatingPssList[i].fullSeat.status === "booked"
      ) {
        // So this seat is already booked at seats io level lets not consider it
        continue;
      }
      // Lets see if we have already seats with status `booked`and the same eventKey
      // so that we push this seat with them
      const bookedObject = seatsStatusChanges.find(
        (st) =>
          st.event === seatingPssList[i].eventKey && st.status === "booked"
      );
      if (bookedObject) {
        // Lets push this seat with the already existing seats with the same `booked`status and the same eventKey
        bookedObject.objects.push({
          objectId: String(seatingPssList[i].seat),
          ticketType: String(seatingPssList[i].uuid),
        });
      } else {
        // so there is no seats with the same `booked` status and the same eventKey, lets create such an object that will hold these kind of seats
        seatsStatusChanges.push({
          event: String(seatingPssList[i].eventKey),
          objects: [
            {
              objectId: String(seatingPssList[i].seat),
              ticketType: String(seatingPssList[i].seat),
            },
          ],
          status: "booked",
          orderId: String(BookingModule.selectedBooking?.reference),
          holdToken: String(
            BookingModule.selectedBooking?.seatsIoHoldToken.holdToken
          ),
        });
      }
    }
  }

  // Now lets go thru seatsStatusChanges array and remove any item with an empty event field or an empty objects field
  const changeObjectStatusPayload: {
    event: string;
    objects: { objectId: string; /*pssUuid*/ ticketType: string }[];
    status: string;
    holdToken?: string;
  }[] = [];

  for (let i = 0; i < seatsStatusChanges.length; i++) {
    if (seatsStatusChanges[i].event && seatsStatusChanges[i].objects.length) {
      changeObjectStatusPayload.push(seatsStatusChanges[i]);
    }
  }

  LogService.info(`Payload built for seats change-object-status call`, {
    params: changeObjectStatusPayload,
  });
  if (!changeObjectStatusPayload.length) {
    // do nothing
    LogService.info(`change-object-status call not required`);
  } else {
    // Lets now cal seats io endpoint to change those seats statuses
    try {
      console.log("--------- calling changeObjectsStatuses");
      const fullSeatData = await changeObjectsStatuses(
        changeObjectStatusPayload
      );
      LogService.info(
        `We got attached response from change-object-status call after manipulation by POS`,
        {
          params: fullSeatData,
        }
      );

      // confirming seats in seats io level is done
      // lets now assign fullSeat a value to each corresponding pss

      if (bookingCopy) {
        const pssList = bookingCopy.pssList;
        for (const key of Object.keys(fullSeatData)) {
          const seatPss = pssList.find(
            (ps) =>
              ((ps.seatType === SeatType.Seat &&
                ps.seat + "-" + ps.uuid === key) ||
                (ps.seatType === SeatType.GeneralAdmissionArea &&
                  ps.seat === key)) &&
              ps.bookingStatus !== BookingStatus.Canceled &&
              ps.bookingStatus !== BookingStatus.Abandoned
          );
          console.log("---------- seatPss");
          console.log(seatPss);
          if (seatPss) {
            // Before adding fullSeat data to pss, lets first check if it is GA, because GA pss objects can be all coming from the same area and eventually having all pss.seat === key
            if (seatPss.seatType === SeatType.Seat) {
              console.log("------- were in Seat type");
              // Lets attach fullSeat data we got from seats io to the pss
              seatPss.fullSeat =
                fullSeatData[String(seatPss.seat) + "-" + String(seatPss.uuid)];
              // Lets attach the status of the seat to the pss itself
              const pssChangeObjectStatus: any = changeObjectStatusPayload.find(
                (p) => p.event === seatPss.eventKey
              );
              if (pssChangeObjectStatus.objects.includes(seatPss.seat)) {
                const seatStatus = pssChangeObjectStatus.status;
                seatPss.fullSeat.status = seatStatus;
              }

              seatPss.updatedAt = nowDB();
            } else if (seatPss.seatType === SeatType.GeneralAdmissionArea) {
              console.log("------- were in GA type");
              // So the pss is actually a GA type
              // this means we can have more pss in the booking object that we should attach data to their fullSeat field because they all come from the same standing are
              // Here we do not identify the right one using seatNumber-pssUuid, we just check if 'key' has the seatNumber (actually the standing area name) in it
              const gaPssList = pssList.filter(
                (ps) =>
                  ps.seat === key &&
                  ps.bookingStatus !== BookingStatus.Canceled &&
                  ps.bookingStatus !== BookingStatus.Abandoned
              );
              console.log("------------ gaPssList");
              console.log(gaPssList);
              if (gaPssList && gaPssList.length) {
                for (let i = 0; i < gaPssList.length; i++) {
                  // Lets attach fullSeat data we got from seats io to the pss
                  gaPssList[i].fullSeat =
                    fullSeatData[String(gaPssList[i].seat)];
                  // Lets attach the status of the seat to the pss itself
                  const pssChangeObjectStatus: any =
                    changeObjectStatusPayload.find(
                      (p) => p.event === gaPssList[i].eventKey
                    );
                  if (
                    pssChangeObjectStatus.objects.includes(gaPssList[i].seat)
                  ) {
                    const seatStatus = pssChangeObjectStatus.status;
                    gaPssList[i].fullSeat.status = seatStatus;
                  }

                  gaPssList[i].updatedAt = nowDB();
                }
              }
            }
          }
        }
      }
    } catch (err) {
      console.log("error when trying to change seats status");
      console.log(err);
      LogService.info(
        `Error when trying to change seats status`,
        { params: err },
        LogLevel.Error
      );
    }
  }
}

const CHART_JS_URL = "https://cdn-eu.seatsio.net/chart.js";
const CHART_JS_SCRIPT_ID = "chartScript";
const CHART_JS_LOAD_TIMEOUT_MS = 20000;

/**
 * Load seats.io chart.js, resolving once `window.seatsio` is usable.
 * The global is the only reliable "loaded" signal: a script tag can exist for a
 * failed load. The retry busts poisoned browser/proxy caches.
 */
export async function loadSeatsioChartJs(): Promise<void> {
  if ((window as any).seatsio) {
    return;
  }

  const urls = [CHART_JS_URL, `${CHART_JS_URL}?retry=${Date.now()}`];
  let lastError: unknown = null;

  for (const url of urls) {
    try {
      await loadChartJsScript(url);
    } catch (err) {
      lastError = err;
      continue;
    }
    if ((window as any).seatsio) {
      return;
    }
    lastError = new Error("seatsio global unavailable after loading chart.js");
  }
  throw lastError;
}

function loadChartJsScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Drop any tag left by a previous failed load
    document.getElementById(CHART_JS_SCRIPT_ID)?.remove();

    const script = document.createElement("script");
    script.id = CHART_JS_SCRIPT_ID;
    script.src = url;
    script.async = true;

    // A hung request must fail instead of loading forever
    const timeoutId = setTimeout(() => {
      script.remove();
      reject(new Error(`Loading ${url} timed out`));
    }, CHART_JS_LOAD_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      script.remove();
      reject(new Error(`Loading ${url} failed`));
    };
    document.body.appendChild(script);
  });
}
